import React from 'react';
import {
  DisplayTasksFilter,
  Preferences,
  PREFERENCES_UPDATED,
  TeamPermission,
  useAuthenticationContextRequired,
  usePreferencesService
} from 'nekst-api';
import { useAuthorizationChecker } from 'authorization-scope';
import {
  CheckboxInput,
  Dropdown,
  FormBlock,
  FormTemplate,
  Header5,
  isReactNative,
  LoadingAnimation,
  SubmitButton,
  Text
} from 'ui-builder';
import { Separator } from 'features/nekst-widgets';
import { useDataLoaderNew } from 'data-loader';

function getDisplayTasksFilterLabel(filter: DisplayTasksFilter): string {
  switch (filter) {
    case DisplayTasksFilter.TODAY:
      return 'Due Today';
    case DisplayTasksFilter.PAST_DUE:
      return 'Past Due';
    case DisplayTasksFilter.NO_DUE_DATE:
      return 'No Due Date';
    case DisplayTasksFilter.ALL:
      return 'All Tasks';
    default:
      return filter;
  }
}

export function PreferencesForm(props: { hideTitle?: boolean }) {
  const preferencesService = usePreferencesService();
  const authenticationContext = useAuthenticationContextRequired();
  const { isGranted } = useAuthorizationChecker();

  const { data: initialData } = useDataLoaderNew(
    async () => {
      return authenticationContext.user._preferences;
    },
    {
      events: [PREFERENCES_UPDATED]
    }
  );

  const displayFilterOptions = [
    {
      value: DisplayTasksFilter.TODAY,
      label: getDisplayTasksFilterLabel(DisplayTasksFilter.TODAY)
    },
    {
      value: DisplayTasksFilter.PAST_DUE,
      label: getDisplayTasksFilterLabel(DisplayTasksFilter.PAST_DUE)
    },
    {
      value: DisplayTasksFilter.NO_DUE_DATE,
      label: getDisplayTasksFilterLabel(DisplayTasksFilter.NO_DUE_DATE)
    },
    {
      value: DisplayTasksFilter.ALL,
      label: getDisplayTasksFilterLabel(DisplayTasksFilter.ALL)
    }
  ];

  if (initialData) {
    return (
      <FormTemplate<Preferences>
        title={!props.hideTitle ? 'Preferences' : undefined}
        initialData={initialData}
        submitFormFunc={preferencesService.savePreferences}
        buttons={<SubmitButton text="Update" />}
      >
        <FormBlock>
          <Header5>Communication Preferences</Header5>
          <Text>
            Configure your email notification preferences.
          </Text>
          <CheckboxInput
            source="isDailyMailEnabled"
            label="Email - Click For a Daily Summary of Your Current, Past Due and Next Day Tasks"
          />
        </FormBlock>

        <Separator />

        <FormBlock>
          <Header5>Tasks Display</Header5>
          <Text>
            Customize how your tasks are displayed by default.
          </Text>
          <CheckboxInput
            source="showCompletedTasks"
            label="Show Completed Tasks by Default"
          />
          {isGranted(TeamPermission.ACCESS_TEAM_MEMBERS_MANAGEMENT) && (
            <CheckboxInput
              source="displayAllTeamMemberTasks"
              label="Display All Team Member Tasks by Default"
            />
          )}
          {!isReactNative() && (
            <CheckboxInput
              source="expandDependentTasks"
              label="Expand Dependent Tasks by Default"
            />
          )}
          <Dropdown
            source="displayListOfTasksInStatus"
            label="When viewing Tasks, default to this list:"
            options={displayFilterOptions}
          />
        </FormBlock>
      </FormTemplate>
    );
  } else {
    return (
      <LoadingAnimation />
    );
  }

}
